class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this._boundClose = this.close.bind(this);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this._boundClose);
    this.setHeaderCartIconAccessibility();
    this.initVariantSwap();
    this.initUpsellArrows();
    this.initImpulseBuy();

    // Listen for bundle upgrade or other external cart updates
    // Don't do a full refreshCartDrawer as that would overwrite bundle script's DOM changes
    document.addEventListener('cart:updated', async (event) => {
      // Skip if we dispatched this event ourselves (from refreshCartDrawer)
      if (event.detail && event.detail.source === 'cart-drawer') return;
      try {
        // Fetch cart state and update dynamic elements the bundle script doesn't touch
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();

        // Update progress bar
        this.updateProgressBarFromCart(cartData);

        // Update subtotal
        const subtotalEl = document.querySelector('.cart-drawer__subtotal-value');
        if (subtotalEl) {
          const formatted = Shopify.currency?.active
            ? (cartData.total_price / 100).toFixed(2) + ' ' + Shopify.currency.active
            : '£' + (cartData.total_price / 100).toFixed(2) + ' GBP';
          subtotalEl.textContent = formatted;
        }

        // Re-render savings banner and impulse buy from section
        const sectionResponse = await fetch(`${window.location.pathname}?sections=cart-drawer`);
        const sections = await sectionResponse.json();
        if (sections['cart-drawer']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');

          // Update savings banner
          const newSavings = doc.querySelector('.cart-drawer__savings-banner');
          const currentSavings = document.querySelector('.cart-drawer__savings-banner');
          const footer = document.querySelector('.drawer__footer');
          if (newSavings && currentSavings) {
            currentSavings.outerHTML = newSavings.outerHTML;
          } else if (newSavings && footer) {
            footer.insertAdjacentHTML('afterbegin', newSavings.outerHTML);
          } else if (!newSavings && currentSavings) {
            currentSavings.remove();
          }

          // Update impulse buy
          const newImpulse = doc.querySelector('.cart-drawer__impulse-buy');
          const currentImpulse = document.querySelector('.cart-drawer__impulse-buy');
          if (newImpulse && currentImpulse) {
            currentImpulse.outerHTML = newImpulse.outerHTML;
          } else if (newImpulse && !currentImpulse) {
            const subtotal = document.querySelector('.cart-drawer__subtotal');
            if (subtotal) subtotal.insertAdjacentHTML('beforebegin', newImpulse.outerHTML);
          } else if (!newImpulse && currentImpulse) {
            currentImpulse.remove();
          }
        }

        // Update delivery estimate
        if (typeof window.updateDeliveryEstimate === 'function') window.updateDeliveryEstimate();

      } catch (e) {
        console.error('Cart update handler failed:', e);
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Fix progress bar fill (Dawn's div:empty rule hides it)
    const progressBar = document.getElementById('cart-progress-bar');
    if (progressBar && progressBar.dataset.progress) {
      progressBar.style.display = 'block';
      progressBar.style.width = progressBar.dataset.progress + '%';
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      this._boundClose = this._boundClose || this.close.bind(this);
      this.querySelector('#CartDrawer-Overlay')?.removeEventListener('click', this._boundClose);
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this._boundClose);
      this.open();
      this.initUpsellArrows();
      // Force progress bar fill on initial add-to-cart
      // Dawn's base CSS has div:empty { display: none } which hides the empty fill div
      const progressBar = document.getElementById('cart-progress-bar');
      if (progressBar && progressBar.dataset.progress) {
        progressBar.style.display = 'block';
        progressBar.style.width = '0%';
        progressBar.offsetWidth; // force reflow
        progressBar.style.width = progressBar.dataset.progress + '%';
      }
      // Re-populate delivery estimate
      if (typeof window.updateDeliveryEstimate === 'function') window.updateDeliveryEstimate();
      // Notify bundle upgrade script to re-add its buttons
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer' } }));
    });
  }

  updateProgressBarFromCart(parsedState) {
    const progressWrapper = document.getElementById('cart-progress-wrapper');
    const progressBar = document.getElementById('cart-progress-bar');
    if (!progressWrapper || !progressBar) return;

    const threshold = parseInt(progressWrapper.dataset.threshold, 10);
    if (!threshold) return;

    const cartTotal = parsedState.total_price || 0;
    const pct = Math.min((cartTotal / threshold) * 100, 100);
    progressBar.style.display = 'block';
    progressBar.style.width = pct + '%';

    if (pct >= 100) {
      progressWrapper.classList.add('full');
    } else {
      progressWrapper.classList.remove('full');
    }

    const goalMessage = progressWrapper.querySelector('.goal-message');
    if (goalMessage) {
      const remaining = threshold - cartTotal;
      const moneyFormat = progressWrapper.dataset.moneyFormat || '£{{amount}}';
      if (remaining > 0) {
        const formatted = moneyFormat.replace('{{amount}}', (remaining / 100).toFixed(2));
        goalMessage.innerHTML = "You're <strong>" + formatted + "</strong> away from free shipping!";
      } else {
        const postMsg = progressWrapper.dataset.postGoalMessage || "You've unlocked free shipping!";
        goalMessage.innerHTML = postMsg;
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ========================================
     VARIANT SWAP — In-cart variant changer
     ======================================== */

  initVariantSwap() {
    this.addEventListener('change', (event) => {
      if (event.target.matches('.cart-drawer__variant-select')) {
        this.handleVariantSwap(event.target);
      }
    });
  }

  async handleVariantSwap(selectElement) {
    const lineIndex = selectElement.dataset.lineIndex;
    const oldVariantId = selectElement.dataset.currentVariantId;
    const newVariantId = selectElement.value;
    const quantity = parseInt(selectElement.dataset.itemQuantity, 10);

    if (newVariantId === oldVariantId) return;

    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const itemCard = selectElement.closest('.cart-drawer__item-card');

    // Check if sold out — revert selection and show error
    if (selectedOption.dataset.available === 'false') {
      selectElement.value = oldVariantId;
      this.showVariantError(itemCard, 'This variant is sold out.');
      setTimeout(() => this.hideVariantError(itemCard), 3000);
      return;
    }

    // Show loading
    this.showItemLoading(itemCard);

    try {
      // Step 1: Remove old variant
      const removeResponse = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: oldVariantId, quantity: 0 }),
      });

      if (!removeResponse.ok) throw new Error('Failed to remove old variant');

      // Step 2: Add new variant at previous quantity
      const addResponse = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(newVariantId), quantity: quantity }] }),
      });

      if (!addResponse.ok) throw new Error('Failed to add new variant');

      // Step 3: Re-render cart drawer
      await this.refreshCartDrawer();
    } catch (error) {
      // Revert dropdown
      selectElement.value = oldVariantId;
      this.showVariantError(itemCard, 'Could not update variant. Please try again.');
      this.hideItemLoading(itemCard);

      // Auto-dismiss after 5 seconds
      setTimeout(() => this.hideVariantError(itemCard), 5000);
    }
  }

  async refreshCartDrawer() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await response.json();

      // Update cart drawer
      const cartDrawerEl = document.getElementById('CartDrawer');
      if (cartDrawerEl && sections['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
        const newContent = doc.querySelector('#CartDrawer');
        if (newContent) {
          cartDrawerEl.innerHTML = newContent.innerHTML;
        }
      }

      // Update cart icon bubble
      const iconBubble = document.getElementById('cart-icon-bubble');
      if (iconBubble && sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.querySelector('.shopify-section');
        if (newBubble) {
          iconBubble.innerHTML = newBubble.innerHTML;
        }
      }

      // Re-bind overlay
      this._boundClose = this._boundClose || this.close.bind(this);
      this.querySelector('#CartDrawer-Overlay')?.removeEventListener('click', this._boundClose);
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this._boundClose);
      this.initUpsellArrows();

      // Update progress bar from current cart state
      try {
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        this.updateProgressBarFromCart(cartData);
      } catch (e) { /* progress bar update is non-critical */ }

      // Re-populate delivery estimate
      if (typeof window.updateDeliveryEstimate === 'function') window.updateDeliveryEstimate();

      // Publish cart update event (source: 'cart-items' prevents CartDrawerItems.onCartUpdate from re-fetching main-cart-items)
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      }

      // Notify bundle upgrade script to re-add its buttons
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer' } }));
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  }

  showSoldOutState(itemCard) {
    const quantityPill = itemCard.querySelector('.cart-drawer__quantity-pill');
    if (quantityPill) {
      quantityPill.style.display = 'none';
      const soldOutLabel = document.createElement('span');
      soldOutLabel.className = 'cart-drawer__sold-out-label';
      soldOutLabel.textContent = 'Sold Out';
      quantityPill.parentNode.insertBefore(soldOutLabel, quantityPill.nextSibling);
    }
  }

  showItemLoading(itemCard) {
    itemCard.style.opacity = '0.5';
    itemCard.style.pointerEvents = 'none';
    itemCard.style.transition = 'opacity 0.2s ease';
  }

  hideItemLoading(itemCard) {
    itemCard.style.opacity = '1';
    itemCard.style.pointerEvents = '';
    itemCard.style.transition = 'opacity 0.2s ease';
  }

  showVariantError(itemCard, message) {
    const errorEl = itemCard.querySelector('.cart-drawer__variant-error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.removeAttribute('hidden');
    }
  }

  hideVariantError(itemCard) {
    const errorEl = itemCard.querySelector('.cart-drawer__variant-error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.setAttribute('hidden', '');
    }
  }

  /* ========================================
     UPSELL ARROWS — Horizontal scroll nav
     ======================================== */

  initUpsellArrows() {
    // Only bind event listeners once — use a flag to prevent duplicates
    if (!this._upsellArrowsBound) {
      this._upsellArrowsBound = true;

      // Use event delegation on the recommendations container so arrows work
      // even if the DOM is replaced after recommendations load
      this.addEventListener('click', (event) => {
        const arrow = event.target.closest('.cart-drawer__upsell-arrow');
        if (!arrow) return;

        const scroller = this.querySelector('[data-upsell-scroll]');
        if (!scroller) return;

        // Scroll by one card width (first card's full width + gap)
        const firstCard = scroller.querySelector('.cart-drawer__upsell-item');
        const gap = 8;
        const scrollAmount = firstCard ? firstCard.offsetWidth + gap : Math.round(scroller.clientWidth * 0.7);
        const direction = arrow.classList.contains('cart-drawer__upsell-arrow--left') ? -scrollAmount : scrollAmount;
        scroller.scrollBy({ left: direction, behavior: 'smooth' });
      });

      // Also re-init arrow states when recommendations finish rendering (they load async)
      this.addEventListener('cart-recommendations-rendered', () => {
        setTimeout(() => this.initArrowStates(), 100);
      });
    }

    // Always refresh arrow disabled states (safe to call multiple times)
    this.initArrowStates();
  }

  initArrowStates() {
    const scroller = this.querySelector('[data-upsell-scroll]');
    const leftArrow = this.querySelector('.cart-drawer__upsell-arrow--left');
    const rightArrow = this.querySelector('.cart-drawer__upsell-arrow--right');
    if (!scroller || !leftArrow || !rightArrow) return;

    const updateStates = () => {
      const atStart = scroller.scrollLeft <= 2;
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
      leftArrow.classList.toggle('cart-drawer__upsell-arrow--disabled', atStart);
      rightArrow.classList.toggle('cart-drawer__upsell-arrow--disabled', atEnd);
    };

    scroller.removeEventListener('scroll', scroller._arrowScrollHandler);
    scroller._arrowScrollHandler = updateStates;
    scroller.addEventListener('scroll', updateStates);
    updateStates();
  }

  initImpulseBuy() {
    if (this._impulseBound) return;
    this._impulseBound = true;

    this.addEventListener('change', async (event) => {
      const checkbox = event.target.closest('[data-impulse-toggle]');
      if (!checkbox) return;

      const variantId = checkbox.dataset.variantId;
      if (!variantId) return;

      checkbox.disabled = true;

      try {
        if (checkbox.checked) {
          // Add to cart
          await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] }),
          });
        } else {
          // Remove from cart
          await fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 0 }),
          });
        }

        await this.refreshCartDrawer();
      } catch (error) {
        console.error('Impulse toggle failed:', error);
        checkbox.checked = !checkbox.checked; // revert on error
      } finally {
        checkbox.disabled = false;
      }
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
